import React from "react";
import {
  Box,
  Typography,
  Button,
  Link,
  Paper,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

// Ouvre le PDF : desktop = nouvel onglet, mobile = téléchargement
const openPdf = (base64Data, filename = "document.pdf") => {
  // Nettoyage base64 (retours ligne + URL-safe)
  let clean = base64Data?.includes(",") ? base64Data.split(",")[1] : base64Data;
  if (!clean) return;
  clean = clean.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  // Convertit en Blob une seule fois
  let bytes;
  try {
    const byteChars = atob(clean);
    bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  } catch (e) {
    console.warn("PDF base64 invalide :", e);
    return;
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  if (isMobile) {
    // 📱 Forcer un téléchargement
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename; // clé pour déclencher le download
    a.target = "_self"; // évite l’onglet
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return;
  }

  // 🖥️ Desktop : ouvrir dans un nouvel onglet (pas d'attribut download)
  const win = window.open("", "_blank");
  if (win) {
    win.opener = null;
    win.location.href = blobUrl;
  } else {
    // si pop-up bloquée, même onglet
    window.location.href = blobUrl;
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

const AssuranceCard = ({
  name,
  description,
  price,
  engagement,
  priseEnCharge,
  features = [],
  pdfIpid,
  pdfNotice,
  onSouscrire,
  onDevis, // non utilisé ici mais conservé si tu l’ajoutes plus tard
}) => {
  const { t } = useTranslation("parcours-client/AssuranceCard");

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: "#F8F8F8",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* HEADER : titre à gauche / prix à droite */}
      <Grid container justifyContent="space-between" alignItems="flex-start">
        <Grid item>
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#4eb933",
              color: "white",
              px: 2,
              py: 0.5,
              borderRadius: "6px",
              fontWeight: 700,
              fontSize: "0.9rem",
            }}
          >
            {name}
          </Box>
          {/* Description sous le titre */}
          <Typography sx={{ mt: 1, fontSize: "0.95rem", color: "#333", mb: 1 }}>
            {description}
          </Typography>
        </Grid>
        <Grid item sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.8rem", color: "#4eb933" }}>
            {price}zł<Box component="span" sx={{ fontSize: "1rem" }}>/mois*</Box>
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#888" }}>
            {t("engagement", { mois: engagement })}
          </Typography>
        </Grid>
      </Grid>

      {/* Couverture */}
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{t("couverture")}</Typography>
      <Box sx={{ mb: 1 }}>
        {features.map((label, i) => (
          <Box
            key={i}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <CheckCircleIcon sx={{ color: "#4eb933", fontSize: "1.1rem", mr: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Prise en charge */}
      {priseEnCharge && (
        <Typography sx={{ fontSize: "0.85rem", color: "#000", mb: 1 }}>
          {t("priseEnCharge", { montant: priseEnCharge })}
        </Typography>
      )}

      {/* Liens PDF + bouton */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          {pdfIpid && (
            <Link
              onClick={() => openPdf(pdfIpid, "IPID.pdf")}
              sx={{ fontSize: "0.85rem", color: "#4eb933", cursor: "pointer" }}
            >
              {t("ficheIpid")}
            </Link>
          )}
          {pdfNotice && (
            <Link
              onClick={() => openPdf(pdfNotice, "Notice.pdf")}
              sx={{ fontSize: "0.85rem", color: "#4eb933", cursor: "pointer" }}
            >
              {t("noticeInformation")}
            </Link>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            onClick={onSouscrire}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#4eb933",
              color: "#fff",
              "&:hover": { backgroundColor: "#3e9e2a" },
            }}
          >
            {t("souscrire")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AssuranceCard;